import { isEmpty } from 'lodash'
import { getTimeDurationString, parseDurationString, roundDurationString } from './timeDuration'
import { estimateMethods } from 'modules/Programs/components/EditWktDurationForm/EstimateMethodSelect'

/*
 * Workout duration estimates.
 *
 * The rules for reading reps, times, holds and per-side wording out of an exercise's
 * instruction text are written up, one rule per section with the problem it fixed, in
 * docs/time-estimate-parsing-rules.md. Read that before changing a reader here.
 *
 * The readers between the SHARED PARSER markers below also run in the mobile app and in
 * the batch scripts. This file is the source of truth: edit here, then run
 * `node tools/syncSharedParser.js` in the timeest-scripts repository to copy the block
 * into the other two. The last section of the rules document explains the setup.
 */

export const LOW_CONFIDENCE_SCORE_THRESHOLD = 0.8
export const LOW_CONFIDENCE_DEVIATION_THRESHOLD = 0.1
export const MAX_WKT_DURATION_SECONDS = 7200 // 2 hours

/* A workout under three minutes is not shown an estimate anywhere: the batch scripts skip it
 * and clear one they wrote earlier, publishing stores none, and the day box shows a dash. Such
 * a total is nearly always a rest day saved as a workout, a placeholder, or a mistake, and
 * "1min" on a card reads as a promise (Vince, 2026-08: "a workout shorter than 180s is prob not
 * a workout and more of a mistake anyway"). Decided for the batch on 2026-08 and applied to the
 * dashboard on 2026-09-10. */
export const MIN_WKT_DURATION_SECONDS = 180
export const isTooShortToShow = (totalSeconds) => !(totalSeconds >= MIN_WKT_DURATION_SECONDS)

/* ====== OVERHEAD CONSTANTS ====== */
const FAMILIARITY_SECONDS_PER_EXERCISE = 12 // 20% chance exercise is new × 60s to learn
const FORM_CUE_SECONDS_PER_EXERCISE = 10 // Reading cues / rewatching video per distinct exercise
const TRANSITION_SECONDS_PER_CHANGE = 4 // Time to switch between exercises (midpoint of 3-5s)
const TIMER_TRANSITION_SECONDS_PER_CHANGE = 1 // Timer exercises already have a 3s countdown in-app

const OVERHEAD_SETUP = { short: 30, medium: 45, long: 60 } // Seconds by workout length tier
const SHORT_WKT_THRESHOLD = 20 * 60 // under 20min
const MEDIUM_WKT_THRESHOLD = 40 * 60 // 20-40min
export const EX_DURATION_FALLBACK = '00:00:45.00'

/**
 * True for a row the coach added and has not filled in yet.
 *
 * "Add exercise" inserts `{ name: '', instructions: '', time: '', rest: '' }`, and until
 * something is typed into it there is no exercise there to estimate. Left to fall through
 * the timing sources below it lands on the 45 second fallback, so an untouched row adds
 * three quarters of a minute to the workout, plus the pacing overhead of a distinct
 * exercise, and counts as a guess — which drags the workout toward the threshold that
 * flags its whole estimate as doubtful.
 *
 * Only genuinely empty rows. The test is whether the row has any content at all, not
 * whether the library knows it:
 *
 *   - An id means the row points at an exercise in the coach's library, so it is real even
 *     with nothing else on it. The edit form relies on this: it holds each row's figures in
 *     maps of its own rather than on the exercise, so its rows are often no more than an id.
 *   - An exercise typed in without matching the library is stored with id '' (and deleting a
 *     library exercise resets the workout's copies to '' too). Those are real exercises
 *     somebody does, and they keep the 45-second guess — their name is what says so.
 *
 * Deciding this on the id alone would drop both, along with the timers and instructions
 * those rows carry, and would make this estimate disagree with the one the mobile app shows
 * for the same workout.
 */
export const isBlankExerciseRow = (ex) => {
  if (!ex) return true
  const hasSetDuration = (value) => Boolean(value) && value !== '00:00:00.00'
  return (
    !ex.id &&
    !ex.name?.trim() &&
    !ex.instructions?.trim() &&
    !hasSetDuration(ex.time) &&
    !hasSetDuration(ex.rest) &&
    isEmpty(ex.timeEst)
  )
}

export const getInflationEnabled = ({ wktDurationInflationEnabled, orgWktDurationInflationEnabled }) => {
  if (wktDurationInflationEnabled !== undefined && wktDurationInflationEnabled !== null) {
    return wktDurationInflationEnabled
  } else if (orgWktDurationInflationEnabled !== undefined && orgWktDurationInflationEnabled !== null) {
    return orgWktDurationInflationEnabled
  } else {
    return true
  }
}

export const getIsLowConfidenceEst = ({ customTimeEst, timerTime, orgTimeEstPerRep, reps, instrTimeEst }) => {
  if (customTimeEst) {
    return false
  } else if (timerTime && timerTime !== '00:00:00.00') {
    return false
  } else if (instrTimeEst && instrTimeEst !== '00:00:00.00') {
    return false
  } else if (orgTimeEstPerRep && reps > 0) {
    return false
  } else {
    return true
  }
}

export const getDefaultEstMethodValue = ({ timerTime, orgTimeEstPerRep, customTimeEstMethod, instrTimeEst }) => {
  if (customTimeEstMethod) {
    return customTimeEstMethod
  } else if (timerTime && timerTime !== '00:00:00.00') {
    return estimateMethods.perSet
  } else if (instrTimeEst && instrTimeEst !== '00:00:00.00') {
    return estimateMethods.perSet
  } else if (orgTimeEstPerRep) {
    return estimateMethods.perRep
  } else {
    return estimateMethods.perSet
  }
}

export const getDefaultEstTimeValue = ({ customTimeEst, timerTime, orgTimeEstPerRep, instrTimeEst }) => {
  if (customTimeEst) {
    return customTimeEst
  } else if (timerTime && timerTime !== '00:00:00.00') {
    return timerTime
  } else if (instrTimeEst && instrTimeEst !== '00:00:00.00') {
    return instrTimeEst
  } else if (orgTimeEstPerRep) {
    return orgTimeEstPerRep
  } else {
    return EX_DURATION_FALLBACK
  }
}

/* ===== SHARED PARSER: START =====
 *
 * Everything between these two markers is copied byte-for-byte into the mobile app
 * (trybe-mobile client/scripts/common/wktDurationUtils.js) and into the batch scripts
 * (timeest-scripts scripts/textParsing.js, generated from this file by
 * tools/syncSharedParser.js there). The scripts' test compares the three blocks and fails when
 * they differ. Edit here, then run the sync; never edit a copy by hand.
 *
 * The block may use only: getTimeDurationString, and what it defines itself.
 */
/* ====== TEXT NORMALIZATION ======
 *
 * Every reader below starts by passing the instructions through this. It rewrites spellings
 * that mean the same thing into one spelling, so the readers only need to know the English,
 * ASCII form. Nothing here decides a number; it only changes how the number is written.
 *
 *   - Long dashes ("5–10") become hyphens. Stripping them once welded "5–10" into 510.
 *   - Curly quotes become straight quotes.
 *   - Inch and foot marks after a digit are time units: 30" is 30 seconds, 5' is 5 minutes.
 *   - "ea" is "each" ("5 ea leg", "10 reps, ea").
 *   - A plus sign between a number and a time unit is dropped: "10+ minutes" is 10 minutes.
 *   - German, Korean, Spanish and French unit words become the English ones, so "20 Sekunden",
 *     "10회", "8 repeticiones por pierna" and "x5 / côté" read like "20 sec", "10 reps",
 *     "8 reps per side" and "x5 /side".
 *
 * Exposed so the mobile app and the batch scripts can be checked against the same table.
 */
const FOREIGN_UNIT_ALIASES = [
  // German
  [/\b(sekunden|sekunde|sek)\b\.?/gi, 'sec'],
  [/\bminuten\b/gi, 'min'],
  [/\b(wiederholungen|wiederholung|wdh)\b\.?/gi, 'reps'],
  [/\b(pro|je)\s+(seite|bein|arm|hand|richtung)\b/gi, 'per side'],
  [/\bhalten\b/gi, 'hold'],
  [/\brunden\b/gi, 'rounds'],
  // Korean (the unit is glued to the digits: 10회, 45초, 3분)
  [/(\d)\s*회/g, '$1 reps'],
  [/(\d)\s*초/g, '$1 sec'],
  [/(\d)\s*분/g, '$1 min'],
  // Spanish and French
  [/\b(repeticiones|repetición|repeticion|répétitions|répétition)\b/gi, 'reps'],
  [/\b(segundos|segundo|secondes|seconde)\b/gi, 'sec'],
  [/\b(minutos|minuto)\b/gi, 'min'],
  // No \b after an accented word: é is not a "word" character to the regex engine (C9).
  [/\b(por|par)\s+(lado|pierna|brazo|côté|cote|jambe|bras)(?![a-zà-ÿ])/gi, 'per side'],
  [/\bde\s+chaque\s+côté(?![a-zà-ÿ])/gi, 'per side'],
  [/\/\s*(côté|cote|lado|seite|bein|jambe|pierna|brazo|bras)(?![a-zà-ÿ])/gi, '/side'],
]

export const normalizeInstructionText = (text = '') => {
  let s = String(text ?? '')
  if (!s) return ''
  s = s.replace(/[–—]/g, '-').replace(/[‘’]/g, "'").replace(/[“”]/g, '"')
  // 30" or 30'' is seconds; 5' is minutes. Only when the mark touches the digits, so quoted
  // prose ("the 'top' position") is left alone. A foot mark followed by a letter is an
  // apostrophe in a word ("Starret's"), not a unit.
  s = s.replace(/(\d)\s*(?:"|''|″)/g, '$1 sec').replace(/(\d)\s*(?:'|′)(?![a-z'])/gi, '$1 min')
  s = s.replace(/\bea\b\.?/gi, 'each')
  s = s.replace(/(\d)\s*\+\s*(?=(?:min|mins|minute|minutes|sec|secs|second|seconds|s)\b)/gi, '$1 ')
  for (const [pattern, replacement] of FOREIGN_UNIT_ALIASES) s = s.replace(pattern, replacement)
  return s
}

/* A time that belongs to "rest" is never the exercise's own time. "10 reps, 1-2 min rest
 * between sets" is ten reps of work; the two minutes are the rest field's business. Both
 * orders are removed: "rest 30 sec" and "30 sec rest" (the rest word touching the time; in
 * "60s, rest as needed" the 60s is the work). The exercise time reader and the
 * dropset series rule run on the stripped text. The coach shorthand's own "10s rest between
 * reps" is read from the original text, where rest counts once per rep by decision. */
const REST_WORDS = '(?:rest|rests|resting|recover|recovery|break)'
const TIME_MENTION =
  '\\d+(?:\\s*(?:-|to)\\s*\\d+)?\\s*(?::\\d{2})?\\s*(?:h|hr|hrs|hour|hours|min|mins|minute|minutes|sec|secs|second|seconds|s)\\b'
const REST_THEN_TIME_RE = new RegExp(`\\b${REST_WORDS}\\b[^.;\\n]{0,20}?${TIME_MENTION}`, 'gi')
const TIME_THEN_REST_RE = new RegExp(`${TIME_MENTION}\\s*(?:of\\s+)?${REST_WORDS}\\b`, 'gi')
export const stripRestMentions = (text = '') =>
  String(text ?? '')
    .replace(REST_THEN_TIME_RE, ' ')
    .replace(TIME_THEN_REST_RE, ' ')

/**
 * Extract per-rep seconds from tempo notation or PNF contract/relax patterns.
 *
 * Handles:
 *   - Standard 4-digit tempo: "1-3-1-1" → 1+3+1+1 = 6s per rep
 *   - Tempo written as one block after the word: "tempo 32X1", "@ 3010", "tempo: 1131".
 *     An X is an explosive phase and counts as one second.
 *   - PNF contract/relax:    "5s / 5s", "10s/5s", "10s-5s" → sum seconds per cycle
 */
const extractTempoFromText = (rawText = '') => {
  if (!rawText) return 0
  const text = normalizeInstructionText(rawText)

  // Pattern 1: Standard 4-digit tempo notation like "1-3-1-1", "2-1-2-3"
  // Must be 4 single digits separated by dashes, not part of a larger number-dash sequence
  const tempoMatch = text.match(/(?:^|[\s,(])(\d)-(\d)-(\d)-(\d)(?=[\s,)]|$)/)
  if (tempoMatch) {
    const sum = parseInt(tempoMatch[1]) + parseInt(tempoMatch[2]) + parseInt(tempoMatch[3]) + parseInt(tempoMatch[4])
    if (sum > 0 && sum <= 30) return sum
  }

  // Pattern 1b: the same four phases written as one block after "tempo" or "@": "32X1", "3010".
  const blockMatch = /(?:tempo|@)\s*:?\s*(\d)(\d)([\dxX])(\d)\b/i.exec(text)
  if (blockMatch) {
    const phase = (c) => (c.toLowerCase() === 'x' ? 1 : parseInt(c, 10))
    const sum = phase(blockMatch[1]) + phase(blockMatch[2]) + phase(blockMatch[3]) + phase(blockMatch[4])
    if (sum > 0 && sum <= 30) return sum
  }

  // Pattern 1c: three phases (down, pause, up) after "tempo" or "@": "tempo 3-2-3". The word
  // is required because "8-6-4 reps" is a rep list.
  const threeMatch = /(?:tempo|@)\s*:?\s*(\d)-(\d)-(\d)(?![\d-])/i.exec(text)
  if (threeMatch) {
    const sum = parseInt(threeMatch[1]) + parseInt(threeMatch[2]) + parseInt(threeMatch[3])
    if (sum > 0 && sum <= 30) return sum
  }

  // Pattern 2: PNF / contract-relax like "5s / 5s", "10s/5s", "10s-5s"
  // Two durations in seconds separated by / or -. Not read when the text is a plus chain
  // ("10r + 10s / 30s"): there the slash separates alternatives, not contract and relax.
  // eslint-disable-next-line
  const pnfMatch = text.includes('+') ? null : text.match(/(\d+)\s*s\s*[\/\-]\s*(\d+)\s*s/i)
  if (pnfMatch) {
    const sum = parseInt(pnfMatch[1]) + parseInt(pnfMatch[2])
    if (sum > 0 && sum <= 60) return sum
  }

  return 0
}

export const extractSetsCount = (text) => {
  if (typeof text !== 'string') return null
  // eslint-disable-next-line
  const regex = /(\d+)\s*sets?\b/i
  const match = text.match(regex)

  // If a match is found, parse the capture group (the number) to an integer
  if (match && match[1]) {
    return parseInt(match[1], 10)
  }

  return null
}

/* ====== COACH SHORTHAND: TIMES ARE COMPONENTS, NOT TOTALS ======
 *
 * Coaches write instructions as shorthand formulas, not prose: "2 times 10s
 * hold", "Dropset: 10s, 8s, 6s", "hold each rep for 3 sec", "10 reps + 10s
 * hold". A time in that shorthand names a COMPONENT of the exercise, almost
 * never the total — and storing the bare literal as the total is exactly how
 * the 09.04 launch undershoots happened (a 3s "total" on a 10-rep brace).
 *
 * Each rule below is a fill-in-the-blank template for one shorthand shape;
 * the arithmetic happens in plain code after the match. The reader answers
 * ONLY on a confident match. Everything else falls through unchanged, so it
 * can fix a wrong number but cannot misread prose it doesn't recognize.
 *
 * HOW THE WHOLE PARSER READS INSTRUCTIONS, by example (each builds on the
 * last). The text is first normalized (normalizeInstructionText: dashes,
 * inch marks, "ea", and German/Korean/Spanish/French unit words become the
 * plain English spelling). Two independent readers then scan the full text:
 * one for reps (extractRepsFromText), one for time (this file's time
 * readers). Within a reader, the first rule that matches wins and later
 * rules never run — but no words are consumed; the other reader still sees
 * the whole text.
 *
 *   Ex 1  "12"                 -> 12 reps. A lone number is reps, never time.
 *                                 Time falls back to the library per-rep value.
 *   Ex 2  "8-12"               -> 12 reps. A range reads as its HIGHEST
 *                                 number. Whole-text only: "8-12kg" reads as
 *                                 nothing. "8+" and "up to 12" are open-ended
 *                                 targets and read the same way.
 *   Ex 3  "do 8-12 reps slowly"-> 12 reps. With a reps-type word next to it,
 *                                 the range no longer needs to stand alone.
 *                                 Up to three words may sit between the number
 *                                 and the rep word ("15 controlled reps"), and
 *                                 tries, breaths, attempts, rotations, taps and
 *                                 cycles count as reps too.
 *   Ex 4  "10 reps, rest 30s"  -> 10 reps. A time that belongs to "rest" is
 *                                 struck out before the time reader runs
 *                                 (stripRestMentions); rest is never work.
 *                                 "Rest 60 seconds" on its own reads nothing.
 *   Ex 4b "30s hold, then 5s"  -> 30s. The PLAIN time reader takes the first
 *                                 time mention as the per-set total; a time
 *                                 range ("30-45 sec") reads as its highest.
 *   Ex 5  "1 set of 10 reps, hold each rep for 3 sec."
 *                              -> a hold on EVERY rep is added to the per-rep
 *                                 time: 10 x (4s + 3s) = 1:10, per-rep method
 *                                 (extractPerRepHoldSeconds; decision R2,
 *                                 2026-09-09). "3 sec hold each" and "hold each
 *                                 rep 5 seconds" read the same way.
 *   Ex 5b "15 reps (1 sec pause up)"
 *                              -> a pause, squeeze or short hold of 5s or less
 *                                 on every rep is ADDED to the per-rep time:
 *                                 15 x (4s + 1s) = 1:15, per-rep method.
 *                                 (extractPerRepPauseSeconds, used by
 *                                 getExEstValues.)
 *   Ex 6  "2 times 10s hold. 10s rest between reps."
 *                              -> "N times Xs hold" composes directly, and a
 *                                 rest mention joins once per rep:
 *                                 2 x (10+10) = 40s. "2-3 times" uses 3.
 *   Ex 7  "15 times bouncing. Last rep 15s hold"
 *                              -> normal reps plus one terminal hold:
 *                                 15 x 4s default + 15s = 1:15.
 *   Ex 8  "Dropset: 10s, 8s, 6s, 4s, 2s hold"
 *                              -> two or more comma-separated times sum: 30s.
 *                                 A slash PAIR ("10s/5s") is PNF tempo
 *                                 notation and belongs to the tempo reader.
 *   Ex 9  "10 Reps + 10 Sec Hold"
 *                              -> a PLUS SIGN joins components and never
 *                                 multiplies: 10 x 4s + 10s = 50s. Every rep
 *                                 term is multiplied by the per-rep time (the
 *                                 tempo when one is written), every time term
 *                                 is added once. "10 + 10s hold on final rep",
 *                                 "10r + 10s", "8-10r + 10s, 2-2-1-0" and
 *                                 "20 Reps + 5 Secs + 5 Secs" all read this way.
 *   Ex 10 "drop set, 20-15-12-8 reps"
 *                              -> 55 reps. A drop set is one continuous set, so
 *                                 its list is summed; so is a bare comma list
 *                                 ("12,10,8,6") and a round scheme ("21-15-9
 *                                 reps"). A per-set list ("Reps: 12/ 10/ 8")
 *                                 reads as its highest number instead: the
 *                                 part's set count supplies the sets.
 *   Ex 11 "3x10"               -> 10 reps. Sets come from the part, so the
 *                                 second number is the count. "3 sets of 8" and
 *                                 "3 x 10 reps" read the same way.
 *   Ex 12 "8-10 each; RPE: 7"  -> 10 reps, per side. A bare "each", a slash
 *                                 ("10 reps/side", "10/side"), "each way",
 *                                 "6 left, 6 right" and an equal pair ("5/5")
 *                                 all mean one side at a time. A lone "left
 *                                 side" row is one side only and does NOT
 *                                 double.
 *
 * PRECEDENCE for the final estimate (getExEstValues), top wins:
 *   1. A time the coach typed into the estimate editor (customTimeEst)
 *   2. A timer set on the exercise
 *   3. A hold, pause or slow phase on every rep (Ex 5, 5b) — per-rep, added to the library time
 *   4. Tempo / PNF notation ("1-3-1-1", "tempo 32X1", "10s/5s") — per-rep
 *   5. This shorthand reader (Ex 6-10) — per-set total
 *   6. A plain time mention (Ex 4b) — per-set total
 *   7. Library per-rep time x reps (only once a rep count was read or typed)
 *   8. The 45-second guess, with the low-confidence banner
 */
const SHORTHAND_DEFAULT_REP_SECONDS = 4
const SECONDS_UNIT = '(?:s|sec|secs|second|seconds)'
const MINUTES_UNIT = '(?:min|mins|minute|minutes)'
const REP_WORD = '(?:reps?|r|repetitions?)'

// A time spent on one phase of a rep: "5 seconds down", "3-5s on the way down", "8 sec
// negative", "eccentric: 6 seconds", "10s up". Such a time is per rep (C1 of the review).
const PHASE_SECONDS = `(\\d+)(?:\\s*(?:-|to)\\s*(\\d+))?\\s*${SECONDS_UNIT}\\b`
const DOWN_WORD = '(?:down(?!\\s*(?:dog|ward))|eccentric|negative|lowering|lower(?!\\s+back)|descent)'
const UP_WORD = '(?:up(?!\\s+to\\b)|concentric)'
const PHASE_GAP = '(?:\\s+(?:on\\s+the\\s+way|going|coming|slow|slowly|controlled))?\\s+'
const PHASE_TIME_RES = [
  { phase: 'down', re: new RegExp(`${PHASE_SECONDS}${PHASE_GAP}${DOWN_WORD}\\b`, 'gi') },
  { phase: 'up', re: new RegExp(`${PHASE_SECONDS}${PHASE_GAP}${UP_WORD}\\b`, 'gi') },
  {
    phase: 'down',
    re: new RegExp(`\\b(?:eccentric|negative|lowering|lower|descent)\\b\\s*(?::|-|of|for)?\\s*${PHASE_SECONDS}`, 'gi'),
  },
  { phase: 'up', re: new RegExp(`\\bconcentric\\b\\s*(?::|-|of|for)?\\s*${PHASE_SECONDS}`, 'gi') },
]

export const stripPhaseMentions = (text = '') =>
  PHASE_TIME_RES.reduce((out, { re }) => out.replace(re, ' '), String(text ?? ''))

export const extractPerRepPhaseSeconds = (rawText = '') => {
  if (!rawText) return null
  const lower = normalizeInstructionText(rawText).toLowerCase()
  const phases = { down: 0, up: 0 }
  let found = false
  for (const { phase, re } of PHASE_TIME_RES) {
    for (const m of lower.matchAll(re)) {
      const isHold = /\b(?:hold|pause)\s+(?:for\s+)?$/.test(lower.slice(0, m.index))
      const seconds = parseInt(m[2] || m[1], 10)
      if (!isHold && seconds > 0 && seconds <= 30) {
        found = true
        phases[phase] = Math.max(phases[phase], seconds)
      }
    }
  }
  return found ? phases : null
}

/* Ex 9. Splits "10 reps + 10s hold + 5 reps" on its plus signs and reads each term as either a
 * count or a time. Returns null when the text is not a plus chain of that kind. */
const readPlusChain = (lower) => {
  if (!lower.includes('+')) return null
  const timeTerm = new RegExp(`^(?:x\\s*)?(\\d+)(?:\\s*(?:-|to)\\s*(\\d+))?\\s*(${SECONDS_UNIT}|${MINUTES_UNIT})\\b`)
  const repsTerm = new RegExp(
    `^(?:x\\s*)?(\\d+)(?:\\s*-\\s*(\\d+))?\\s*(?:${REP_WORD}\\b)?(?!\\s*(?:kg|lbs?|%|${SECONDS_UNIT}\\b|${MINUTES_UNIT}\\b|[x×]\\s*\\d))`
  )
  const highOf = (lo, hi) => Math.max(parseInt(lo, 10), hi ? parseInt(hi, 10) : 0)
  let reps = 0
  let seconds = 0
  let timeTerms = 0
  for (const rawTerm of lower.split('+')) {
    const term = rawTerm.trim()
    let m = timeTerm.exec(term)
    if (m) {
      const value = highOf(m[1], m[2])
      seconds += /^m/.test(m[3]) ? value * 60 : value
      timeTerms++
      continue
    }
    m = repsTerm.exec(term)
    if (m) reps += highOf(m[1], m[2])
  }
  return { reps, seconds, timeTerms }
}

export const extractTimeFromCoachShorthand = (rawText = '', { perRepSeconds } = {}) => {
  if (!rawText) return undefined
  const text = normalizeInstructionText(rawText)
  const lower = text.toLowerCase()
  const secondsOf = (m) => parseInt(m, 10) || 0
  // A rep range reads as its highest number (same rule as extractRepsFromText).
  const highOf = (lo, hi) => (hi ? parseInt(hi, 10) : parseInt(lo, 10))
  const toTimeStr = (totalSeconds) => getTimeDurationString(Math.round(totalSeconds) * 1000)
  // What one rep costs when the shorthand needs it: the written tempo, else the library
  // figure the caller passes in, else the 4-second floor.
  const repSeconds = extractTempoFromText(text) || perRepSeconds || SHORTHAND_DEFAULT_REP_SECONDS

  // G0 (Ex 9): a plus chain. Needs at least one time term, and either a rep term or a second
  // time term, so "2+2+2" (reps only) and "5s" alone never fire.
  const chain = readPlusChain(lower)
  if (chain && chain.timeTerms >= 1 && (chain.reps > 0 || chain.timeTerms >= 2)) {
    const total = chain.reps * repSeconds + chain.seconds
    if (total > 0) return toTimeStr(total)
  }

  // G1b (C2): "3 x 10s", "3x30 seconds", "4 x 5-10s", "5 sec x3", "10 sec hang, rest x3". A
  // count times a time multiplies, with or without the word "hold". Read on the rest-free
  // text so the rest is never the time. "x N sets" is the part's set count, not a multiplier,
  // and a count before a hold is left to G2, which adds a rest between reps.
  const restFreeText = stripRestMentions(lower)
  const TIMED = `(\\d+)(?:\\s*(?:-|to)\\s*(\\d+))?\\s*(${SECONDS_UNIT}|${MINUTES_UNIT})\\b`
  const countThenTime = new RegExp(
    `(?:^|[^\\d.:-])(\\d+)(?:\\s*-\\s*(\\d+))?\\s*(?:x|times)\\s*${TIMED}(?!\\s+hold)`
  ).exec(restFreeText)
  const timeThenCount = countThenTime
    ? null
    : new RegExp(
        `${TIMED}[^.;\\d]{0,25}?(?:x|times)\\s*(\\d+)(?:\\s*-\\s*(\\d+))?(?!\\s*(?:sets?|rounds?|\\d|[a-z-]))`
      ).exec(restFreeText)
  const timed = countThenTime
    ? {
        count: highOf(countThenTime[1], countThenTime[2]),
        time: highOf(countThenTime[3], countThenTime[4]),
        unit: countThenTime[5],
      }
    : timeThenCount && {
        count: highOf(timeThenCount[4], timeThenCount[5]),
        time: highOf(timeThenCount[1], timeThenCount[2]),
        unit: timeThenCount[3],
      }
  if (timed && timed.count > 0 && timed.time > 0) {
    const total = timed.count * timed.time * (/^m/.test(timed.unit) ? 60 : 1)
    if (total < 3600) return toTimeStr(total)
  }

  // G1 (Ex 8): a series of second-durations sums ("Dropset: 10s, 8s, 6s, 4s, 2s"). Read on
  // the rest-free text so "Rest 30s, 45s, or 60s" is a list of rests, not a series.
  // A slash-only PAIR is excluded: "10s/5s" is PNF contract/relax notation,
  // which is per-rep tempo (the tempo extractor's job), not a series.
  const series = restFreeText.match(/\b\d+\s*s(?:ec\w*)?\s*(?:[,/]\s*\d+\s*s(?:ec\w*)?\s*)+/)
  if (series && (series[0].includes(',') || series[0].match(/\d+/g).length >= 3)) {
    const parts = series[0].match(/\d+/g).map(secondsOf)
    const total = parts.reduce((a, b) => a + b, 0)
    if (total > 0) return toTimeStr(total)
  }

  // Shared piece for G2 and G3. Read from the original text: inside these templates a rest
  // between reps is part of the set (decision 3), not something to strike out.
  const restMatch = /(\d+)\s*s(?:ec\w*)?\s+rest/.exec(lower)
  const restSeconds = restMatch ? secondsOf(restMatch[1]) : 0

  // A hold on the last rep, in either word order, added once on top of whatever else the
  // text composes: "5 times 5s hold. Last rep 30s hold" is 5 x 5s + 30s.
  const lastRepHoldMatch =
    /last\s+rep\s+(\d+)\s*s(?:ec\w*)?\s+hold/.exec(lower) ||
    /hold\s+(?:the\s+)?last\s+(?:rep|one)\s+(?:for\s+)?(\d+)\s*s(?:ec\w*)?\b/.exec(lower)
  const lastRepHoldSeconds = lastRepHoldMatch ? secondsOf(lastRepHoldMatch[1]) : 0

  // G2 (Ex 6): "<N[-M]> x|times|reps ... <X>s hold" — reps of a timed hold. The last-rep hold
  // is not the per-rep hold, so the search stops at a sentence end.
  const repsHold = /(\d+)(?:\s*-\s*(\d+))?\s*(?:x|times?|reps?\b(?:\s+of)?)[^.;]*?(\d+)\s*s(?:ec\w*)?\s+hold/.exec(
    lower
  )
  if (repsHold) {
    const reps = highOf(repsHold[1], repsHold[2])
    const hold = secondsOf(repsHold[3])
    if (reps > 0 && hold > 0) return toTimeStr(reps * (hold + restSeconds) + lastRepHoldSeconds)
  }

  // G4 (Ex 7): "last rep <X>s hold" or "hold the last rep for <X>s" — reps of normal work plus
  // a terminal hold.
  if (lastRepHoldSeconds > 0) {
    const reps = extractRepsFromText(text)
    if (reps > 0) return toTimeStr(reps * repSeconds + lastRepHoldSeconds)
  }

  return undefined
}

/* Ex 5 (R2, 2026-09-09). A hold on every rep: "hold each rep for 3 sec", "hold each rep 5 seconds",
 * "3 sec hold each", "hold for 3 sec each rep", "+ 3 sec hold each". The rep is still performed
 * around the hold, so the hold is ADDED to the per-rep time, the same as a pause or a slow
 * phase. Returns the seconds (a range reads its highest), or 0. Distinct from "N times Xs hold"
 * (rule 10), where each rep IS a hold. */
const PER_REP_HOLD_SECONDS = `(\\d+)(?:\\s*(?:-|to)\\s*(\\d+))?\\s*${SECONDS_UNIT}\\b`
// "each" or "every" on its own, or "per rep". Not "per position", "each side", "per leg": there
// the hold is the set, done once per position or side (rules 10 and 20).
const PER_REP_CUE =
  '(?:(?:each|every)(?:\\s+rep)?|per\\s+rep)\\b(?!\\s+(?:side|leg|arm|hand|direction|position|way|set))'
const PER_REP_HOLD_RES = [
  // "hold each rep for 3s", "holding each stretch for 3s", "hold each for 3s"
  new RegExp(`\\bhold(?:ing)?\\s+each(?:\\s+(?!for\\b)[a-z]+)?\\s+(?:for\\s+)?${PER_REP_HOLD_SECONDS}`, 'gi'),
  new RegExp(`${PER_REP_HOLD_SECONDS}\\s+hold\\s+(?:on\\s+)?${PER_REP_CUE}`, 'gi'),
  new RegExp(`\\bhold\\s+(?:for\\s+)?${PER_REP_HOLD_SECONDS}\\s+(?:on\\s+)?${PER_REP_CUE}`, 'gi'),
]

export const extractPerRepHoldSeconds = (rawText = '') => {
  if (!rawText) return 0
  const lower = normalizeInstructionText(rawText).toLowerCase()
  for (const pattern of PER_REP_HOLD_RES) {
    const m = new RegExp(pattern.source, 'i').exec(lower)
    if (m) {
      const seconds = parseInt(m[2] || m[1], 10)
      return seconds > 0 && seconds <= 120 ? seconds : 0
    }
  }
  return 0
}

// The time reader must not take a per-rep hold as the set: with no rep count, "hold each rep
// for 3 sec" reads nothing and falls to the guess, like a bare phase time.
export const stripPerRepHoldMentions = (text = '') =>
  PER_REP_HOLD_RES.reduce((out, re) => out.replace(new RegExp(re.source, 'gi'), ' '), String(text ?? ''))

/* Ex 5b. A pause, squeeze or short hold that happens on every rep. Returns the seconds when
 * the text names one of five seconds or less, else 0. Longer holds are the shorthand's
 * business (Ex 5, 6); a five-second cap keeps "hold 30 sec at the top" out of here. */
export const extractPerRepPauseSeconds = (rawText = '') => {
  if (!rawText) return 0
  const lower = normalizeInstructionText(rawText).toLowerCase()
  const S = `(\\d+)(?:\\s*(?:-|to)\\s*(\\d+))?\\s*${SECONDS_UNIT}\\b`
  const CUE = '\\b(?:each|every|per)\\s+(?:rep|position)\\b|\\b(?:up|down|top|bottom)\\b'
  const patterns = [
    new RegExp(`${S}[^.;\\n\\d]{0,30}?\\b(?:pause|squeeze|stop)`),
    new RegExp(`\\b(?:pause|squeeze|stop)\\b[^.;\\n\\d]{0,30}?${S}`),
    new RegExp(`${S}[^.;\\n\\d]{0,30}?\\bhold\\b[^.;\\n]{0,40}?(?:${CUE})`),
    new RegExp(`\\bhold\\b[^.;\\n\\d]{0,30}?${S}[^.;\\n]{0,40}?(?:${CUE})`),
  ]
  for (const pattern of patterns) {
    const m = pattern.exec(lower)
    if (m) {
      const seconds = parseInt(m[2] || m[1], 10)
      if (seconds > 0 && seconds <= 5) return seconds
      return 0
    }
  }
  return 0
}

// ====== PER-SIDE ("one side at a time") DETECTION ======
//
// One definition, kept identical in creator-dashboard, trybe-mobile and the backfill scripts.
// The rep readers and the per-side check share a noun list because separate copies drifted:
// "10 per hand" was counted but never doubled, "10 reps L/R" was doubled but never counted.

const PER_SIDE_NOUNS = 'side|leg|arm|hand|direction|way'

// "5 per side", "6 breaths per side" — the count is for one side. No digits in the gap, or
// "3 sets of 10 per side" reads as 3. Plural but no further, or "10 each armchair" matches.
// A set count is never the per-side figure: "1 set per leg, 6 reps" is six.
// eslint-disable-next-line
// A time is never the count: "20 sec per side" reads no reps, the time reader has it (C10).
const PER_SIDE_REPS_RE = new RegExp(
  `(\\d+)\\s+(?:(?!(?:sets?|s|secs?|seconds?|mins?|minutes?|hrs?|hours?)\\b)[a-z]+\\s+)*?(?:per|each)\\s+(?:${PER_SIDE_NOUNS})s?\\b`,
  'i'
)

// The short spellings of the same thing (Ex 12): "10 reps/side", "10/side", "5 reps/each",
// a bare "each" closing the count ("8-10 each; RPE: 7", "10 reps, ea"), "6 left, 6 right", and an equal
// pair ("5/5", "10,10"). Each captures the one-side count in group 1 (and a range's high
// end in group 2 where a range is possible).
// eslint-disable-next-line
const SLASH_SIDE_REPS_RE = new RegExp(
  `(\\d+)(?:\\s*-\\s*(\\d+))?\\s*(?:reps?|r|repetitions?)?\\s*\\/\\s*(?:each\\s+)?(?:${PER_SIDE_NOUNS}|each)s?\\b`,
  'i'
)
// eslint-disable-next-line
const BARE_EACH_REPS_RE = /(\d+)(?:\s*-\s*(\d+))?\s*(?:reps?|r|repetitions?)?\s*,?\s+each\s*(?:[;,.)\]]|$|rpe|rir)/i
// eslint-disable-next-line
const LEFT_RIGHT_REPS_RE = /(\d+)\s*(?:reps?\s*)?(?:left|right)\b[^\n]{0,8}?(\d+)\s*(?:reps?\s*)?(?:left|right)\b/i
// eslint-disable-next-line
const EQUAL_PAIR_REPS_RE = /^\s*(\d{1,3})\s*[\/,]\s*(\d{1,3})\s*$/

// "5 L/R" — the same thing said short. Without this it reads as no reps at all, and no reps is
// what sends an exercise to the model for a flat time per set it can never get back off.
// eslint-disable-next-line
const L_R_REPS_RE = new RegExp(`(\\d+)\\s+(?:[a-z]+\\s+)*?\\bL\\s*\\/\\s*R\\b`, 'i')

// Per-side with or without a count. The \b around L/R matter: it is case-insensitive, so
// without them it fires inside ordinary words such as "curl/roll".
// eslint-disable-next-line
const IS_PER_SIDE_RE = new RegExp(
  [
    `(?:per|each)\\s+(?:${PER_SIDE_NOUNS})s?\\b`,
    `\\bL\\s*\\/\\s*R\\b`,
    SLASH_SIDE_REPS_RE.source,
    // A time before the slash: "60 sec/side", "30s/leg", "1 min/side".
    `\\d+(?:[.,]\\d+)?\\s*(?:s|sec|secs|second|seconds|min|mins|minute|minutes)\\s*\\/\\s*(?:${PER_SIDE_NOUNS})s?\\b`,
    BARE_EACH_REPS_RE.source,
    LEFT_RIGHT_REPS_RE.source,
  ].join('|'),
  'i'
)

export const isPerSideText = (text) => {
  if (!text) return false
  const normalized = normalizeInstructionText(text)
  if (IS_PER_SIDE_RE.test(normalized)) return true
  const pair = EQUAL_PAIR_REPS_RE.exec(normalized)
  return Boolean(pair && pair[1] === pair[2])
}

// Exposed so the three copies can be tested against each other.
export const perSidePatternSources = {
  nouns: PER_SIDE_NOUNS,
  perSideReps: PER_SIDE_REPS_RE.source,
  lrReps: L_R_REPS_RE.source,
  isPerSide: IS_PER_SIDE_RE.source,
}

const repCore = ['r', 'x', 're', 'rep', 'reps', 'repetition', 'repetitions']
const repGeneric = ['move', 'moves', 'movement', 'movements', 'action', 'actions', 'count', 'counts', 'times']
// Things coaches count that are one rep each: "10 tries", "5 breaths", "20 taps", "3 cycles".
// "ones" only ever follows a description ("10 slow and controlled ones").
const repCounted = [
  'try',
  'tries',
  'breath',
  'breaths',
  'attempt',
  'attempts',
  'rotation',
  'rotations',
  'contraction',
  'contractions',
  'tap',
  'taps',
  'cycle',
  'cycles',
  'alternating',
  'ones',
]
const repGymnastics = [
  'lean',
  'leans',
  'circle',
  'circles',
  'swing',
  'swings',
  'rock',
  'rocks',
  'pulse',
  'pulses',
  'bounce',
  'bounces',
  'roll',
  'rolls',
  'reach',
  'reaches',
  'step',
  'steps',
]
const repStrength = ['lift', 'lifts', 'pull', 'pulls', 'press', 'presses', 'push', 'pushes', 'drive', 'drives']
const repConditioning = [
  'jump',
  'jumps',
  'hop',
  'hops',
  'throw',
  'throws',
  'catch',
  'catches',
  'kick',
  'kicks',
  'strike',
  'strikes',
  'sprint',
  'sprints',
]
export const repsTextToMatch = [
  ...repCore,
  ...repGeneric,
  ...repGymnastics,
  ...repStrength,
  ...repConditioning,
  ...repCounted,
]
export const extractRepsFromText = (rawText = '') => {
  /* Normalize first (long dashes, inch marks, "ea", foreign unit words). Stripping "5–10"
   * used to weld the digits into 510, the single worst misread the launch surfaced. */
  const dashSafe = normalizeInstructionText(rawText)
  // Structural patterns run on the lowercased text; the character-stripped copy is for the
  // unit-word patterns, which came first and expect it.
  // A percentage is an intensity, never a count: "4x8 @ 70%", "60% - 70% from your max reps".
  const lowerText = dashSafe.toLowerCase().replace(/\d+(?:[.,]\d+)?\s*%/g, ' ')
  // eslint-disable-next-line
  const normalizedText = lowerText.replace(/[^a-z0-9:\-\/\,\.\sx]/gi, '')

  const allUnitAliases = repsTextToMatch.join('|')
  // What may follow a unit word: space, punctuation, a colon ("5 reps: tempo 32X1"), a
  // closing bracket, or the end.
  const AFTER_UNIT = '(?=[\\s.,;:)\\]]|$)'
  const highOf = (lo, hi) => {
    const a = parseInt(lo, 10)
    const b = hi ? parseInt(hi, 10) : a
    return b >= a ? b : a
  }
  const sumOf = (nums) => nums.reduce((a, b) => a + b, 0)

  let match

  // -------------------------
  // Pass L: LISTS of rep counts (Ex 10).
  // L1 A drop set is one continuous set: sum the longest run of numbers.
  // L2 A round scheme ("21-15-9 reps", three or more numbers joined by dashes): sum.
  // L3 A bare comma list as the whole text ("12,10,8,6") is a per-set list and reads its
  //    highest number (R1, 2026-09-09: 89% of them sit in parts with two or more sets). An
  //    equal pair ("10,10") is one count per side and is left to Pass 0P.
  // Per-set lists ("Reps: 12/ 10/ 8", "12, 10, 8 reps") read as their highest number in
  // Passes 1 and 2 below; the part's set count supplies the sets.
  // -------------------------
  if (/drop\s*-?\s*sets?\b/.test(lowerText)) {
    const runs = lowerText.match(/\d+(?:\s*[,/-]\s*\d+)+/g) || []
    const longest = runs.map((run) => run.match(/\d+/g).map(Number)).sort((a, b) => b.length - a.length)[0]
    if (longest && longest.length >= 2) {
      const total = sumOf(longest)
      if (total > 0 && total <= 500) return total
    }
  }
  const roundScheme = new RegExp(`(\\d+(?:\\s*-\\s*\\d+){2,})\\s*(?:reps?|r|repetitions?)\\b`).exec(lowerText)
  if (roundScheme) {
    const total = sumOf(roundScheme[1].match(/\d+/g).map(Number))
    if (total > 0 && total <= 500) return total
  }
  const bareList = /^\s*(\d{1,3}(?:\s*,\s*\d{1,3})+)\s*\.?\s*$/.exec(lowerText)
  if (bareList) {
    const nums = bareList[1].match(/\d+/g).map(Number)
    if (!(nums.length === 2 && nums[0] === nums[1])) {
      const highest = Math.max(...nums)
      if (highest > 0) return highest
    }
  }

  // -------------------------
  // Pass 0P: the short per-side spellings (Ex 12). The count is for one side; doubling is
  // isPerSideText's job. "5/5" and "10,10" are one count done on each side.
  // -------------------------
  if ((match = EQUAL_PAIR_REPS_RE.exec(lowerText)) !== null && match[1] === match[2]) return parseInt(match[1], 10)
  if ((match = LEFT_RIGHT_REPS_RE.exec(lowerText)) !== null)
    return Math.max(parseInt(match[1], 10), parseInt(match[2], 10))
  if ((match = SLASH_SIDE_REPS_RE.exec(lowerText)) !== null) return highOf(match[1], match[2])
  if ((match = BARE_EACH_REPS_RE.exec(lowerText)) !== null) return highOf(match[1], match[2])

  // -------------------------
  // The per-side count is read before any other count (C14 of the review). A text can name
  // a total and the per-side figure it is made of — "Reps: up to 20 (10 each side)" — and
  // the flag doubles whatever is read, so the figure read has to be the one for one side.
  // -------------------------
  // -------------------------
  // Pass 0a: "N per side/leg/arm/each/direction" pattern
  // Examples: "5 per side", "3 per leg", "10 each arm", "6 breaths per side"
  // Allows optional words between number and per/each
  // -------------------------
  if ((match = PER_SIDE_REPS_RE.exec(lowerText)) !== null) {
    const value = parseInt(match[1], 10)
    if (!isNaN(value) && value > 0) return value
  }

  // -------------------------
  // Pass 0b: "N L/R" pattern — a per-side count written the short way
  // Examples: "5 L/R", "10 reps L/R"
  // -------------------------
  if ((match = L_R_REPS_RE.exec(lowerText)) !== null) {
    const value = parseInt(match[1], 10)
    if (!isNaN(value) && value > 0) return value
  }

  // -------------------------
  // Pass 0L: a bare count followed by one side — "10 left side", "8-10 right", "12 left leg".
  // The count for that side; the row covers one side only, so nothing doubles it (C5).
  // -------------------------
  if (
    (match =
      /^\s*(\d{1,3})(?:\s*-\s*(\d{1,3}))?\s*(?:reps?)?\s*[,(]?\s*(?:left|right|first|second|other|one)(?:\s+(?:side|leg|arm|hand|foot))?\s*[.)]?\s*$/.exec(
        lowerText
      )) !== null
  ) {
    return highOf(match[1], match[2])
  }

  // -------------------------
  // Pass 0H: a bare count in front of a per-rep hold — "3 with 5s hold on each", "6 with
  // 5-10s hold on each". The hold says what the count is a count of (Lukas's C4).
  // -------------------------
  if (
    (match =
      /^\s*(\d{1,3})(?:\s*-\s*(\d{1,3}))?\s*(?:with|,|;|:)?\s*(?:a\s+)?\d+(?:\s*-\s*\d+)?\s*(?:s|sec|secs|seconds?)\s+hold\s+(?:on\s+)?(?:each|every)\b/.exec(
        lowerText
      )) !== null
  ) {
    return highOf(match[1], match[2])
  }

  // -------------------------
  // Pass 0U: open-ended counts — "Reps: up to 20", "8+". The target is the estimate, the
  // same way a range reads as its highest number.
  // -------------------------
  if ((match = /\bup\s+to\s+(\d+)\b/.exec(lowerText)) !== null) return parseInt(match[1], 10)
  if ((match = /^\s*(\d{1,3})\s*\+\s*(?:reps?)?\s*$/.exec(lowerText)) !== null) return parseInt(match[1], 10)

  // -------------------------
  // Pass 0R: rep RANGES — "5-10 reps", "do 5-10 reps of", "8-12 times".
  // A range reads as its HIGHEST number (product decision 09.07: estimate
  // for the full prescription). Runs before the structured pass so the
  // en-dash form behaves identically to the hyphen form.
  // -------------------------
  const rangeWithUnit = new RegExp(`(\\d+)\\s*-\\s*(\\d+)\\s*(?:${allUnitAliases})${AFTER_UNIT}`, 'i')
  if ((match = rangeWithUnit.exec(normalizedText)) !== null) {
    const lo = parseInt(match[1], 10)
    const hi = parseInt(match[2], 10)
    if (!isNaN(lo) && !isNaN(hi) && hi >= lo && lo > 0) return hi
  }

  // -------------------------
  // Pass 0N: the instruction IS just a number or a bare range — "8-10", "12".
  // With nothing else written, a bare number or range defaults to reps (not
  // some other metric); a range reads as its highest number. Whole-text
  // match only, capped at 100, so "8-10kg" or longer prose never trips it.
  // -------------------------
  const bareNumberOrRange = /^\s*(\d{1,3})(?:\s*-\s*(\d{1,3}))?\s*$/.exec(dashSafe)
  if (bareNumberOrRange) {
    const lo = parseInt(bareNumberOrRange[1], 10)
    const hi = bareNumberOrRange[2] ? parseInt(bareNumberOrRange[2], 10) : lo
    if (hi > 0 && hi <= 100 && hi >= lo) return hi
  }

  // -------------------------
  // Pass 0c: "N + Xs per side" pattern (PNF with leading rep count)
  // Examples: "3 + 15s per side", "5 + 10s"
  // -------------------------
  const plusHoldPattern = /(\d+)\s*\+\s*\d+\s*s/i
  if ((match = plusHoldPattern.exec(lowerText)) !== null) {
    const value = parseInt(match[1], 10)
    if (!isNaN(value) && value > 0 && value <= 30) return value
  }

  // -------------------------
  // Pass 0X: a count followed by a bracketed set count — "65% - 3 (2x)", "8 (3x)". The
  // number before the bracket is the count; the bracket is how many times it is done.
  // -------------------------
  if ((match = /(\d+)(?:\s*-\s*(\d+))?\s*\(\s*\d+\s*[x×]\s*\)/.exec(lowerText)) !== null) {
    return highOf(match[1], match[2])
  }

  // -------------------------
  // Pass 0S: sets written into the text (Ex 11) — "3x10", "3 x 10 reps", "3 sets of 8",
  // "4x8 @ 70%". The second number is the count; the part supplies the sets. Not when the
  // second number is a weight, a time or a distance ("10 x 20kg", "2x 30 second hold"), and
  // not a tempo block ("31X1", "tempo 3010"), which is four characters with no space.
  // -------------------------
  const tempoBlock = /(?:tempo|@)\s*:?\s*\d[\dx]{2}\d\b/i.test(dashSafe) || /\b\d\d[X]\d\b/.test(dashSafe)
  // "100 / 2x50 / 4x25 reps" lists ways of reaching one total; the total is the count.
  const alternativeSpelling = /\/\s*\d+\s*[x×]\s*\d+/.test(lowerText)
  // Written the other way round, "20 x 3 sets", the first number is the count.
  const repsBySets = /(\d+)(?:\s*-\s*(\d+))?\s*(?:x|×|times)\s*\d+(?:\s*-\s*\d+)?\s*(?:sets?|rounds?)\b/.exec(lowerText)
  if (repsBySets && !alternativeSpelling) {
    const value = highOf(repsBySets[1], repsBySets[2])
    if (value > 0) return value
  }
  const setsByReps = new RegExp(
    `(\\d+)\\s*(?:sets?\\s*(?:of|x|×)|[x×]|times)\\s*(\\d+)(?:\\s*-\\s*(\\d+))?(?!\\d)(?!\\s*(?:kg|lbs?\\b|%|s\\b|sec|secs|second|seconds|min|mins|minute|minutes|m\\b|meters?|metres?|km|yards?|ft|feet))`
  ).exec(lowerText)
  if (setsByReps && !tempoBlock && !alternativeSpelling) {
    const value = highOf(setsByReps[2], setsByReps[3])
    if (value > 0) return value
  }

  // -------------------------
  // Pass 1: Structured format
  // Examples:
  // "10 reps"
  // "12 / 10 / 8 reps"  (a per-set list: its highest number)
  // "12, 10, 8 reps", "12,10,8 and 6 reps"
  // "10x reps"
  // "15 controlled reps", "10 slow and controlled ones"  (up to three words between)
  // -------------------------
  // Up to three describing words may sit between the count and the rep word. Joining words
  // may not: "60 - 70 from your max reps" is not seventy reps. Nor may a single letter or a
  // time unit: "60s alternating" is a minute, not sixty reps.
  // The list group requires a separator between numbers. Written as "(\\d+ sep?)+", a run of
  // digits could be split in exponentially many ways, and twenty-odd numbers in a row froze
  // the browser tab for seconds (fixed 2026-09-09; the pattern dated from before this work).
  const BRIDGE_WORD =
    '(?:(?!(?:from|of|to|for|per|at|with|the|then|or|sets?|rounds?|min|mins|minutes?|sec|secs|seconds?|hrs?|hours?|kg|lbs?)\\b)[a-z]{2,}\\s+)'
  const structuredPattern = new RegExp(
    `(\\d+(?:\\s*(?:[\\/\\,x]|and)\\s*\\d+)*)(?:\\s*[\\/\\,])?\\s*-?\\s*${BRIDGE_WORD}{0,3}?(${allUnitAliases})${AFTER_UNIT}`,
    'gi'
  )

  if ((match = structuredPattern.exec(normalizedText)) !== null) {
    const values = match[1]
      .trim()
      .split(/[/,x]|and/)
      .map((v) => parseInt(v, 10))
      .filter((v) => !Number.isNaN(v))
    if (values.length > 0) return Math.max(...values)
  }

  // -------------------------
  // Pass 2: Key-value format
  // Examples:
  // "reps: 12"
  // "reps 12 / 10 / 8"   (a per-set list: its highest number)
  // "Reps: 8-12"         (a range: its highest number)
  // -------------------------
  const keyValuePattern = new RegExp(
    `\\b(${allUnitAliases})\\s*[:\\-=]?\\s*(\\d+(?:\\s*-\\s*\\d+)?(?:\\s*[\\/\\,x]\\s*\\d+(?:\\s*-\\s*\\d+)?)*)`,
    'gi'
  )

  if ((match = keyValuePattern.exec(normalizedText)) !== null) {
    const values = match[2]
      .trim()
      .split(/[/,x]/)
      .map((part) => {
        const range = /(\d+)(?:\s*-\s*(\d+))?/.exec(part)
        return range ? highOf(range[1], range[2]) : NaN
      })
      .filter((v) => !Number.isNaN(v))
    if (values.length > 0) return Math.max(...values)
  }

  return 0
}

function createRegexFromUnits(units) {
  const unitPattern = units.join('|')
  return new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*-?\\s*(${unitPattern})\\b`, 'gi')
}

const timeTextMatchToUnit = {
  h: 'hr',
  hr: 'hr',
  hrs: 'hr',
  hour: 'hr',
  hours: 'hr',
  min: 'min',
  mins: 'min',
  minute: 'min',
  minutes: 'min',
  sec: 'sec',
  secs: 'sec',
  second: 'sec',
  seconds: 'sec',
  s: 'sec',
}

export const extractTimeValueFromText = (rawText = '') => {
  let text = normalizeInstructionText(rawText)
  if (!text) return undefined

  // A clock time standing alone ("3:00", "1:30 hold") is minutes and seconds. Two digits
  // after the colon are required: "1:1" is a ratio.
  const bareClock = /^\s*(\d{1,2}):(\d{2})\s*(?:hold|min|mins|minutes?)?\s*\.?\s*$/i.exec(text)
  if (bareClock) return `00:${bareClock[1].padStart(2, '0')}:${bareClock[2]}.00`

  // A time range reads as its highest number, the same decision as for reps: "30-45 sec",
  // "10 Secs - 30 Secs Per Stretch", "10 to 20 seconds" all read the second figure.
  text = text.replace(
    /(\d+(?:[.,]\d+)?)\s*(?:h|hr|hrs|hour|hours|min|mins|minute|minutes|sec|secs|second|seconds|s)?\s*(?:-|to)\s*(\d+(?:[.,]\d+)?)\s*(h|hr|hrs|hour|hours|min|mins|minute|minutes|sec|secs|second|seconds|s)\b/gi,
    '$2 $3'
  )

  // otherwise try to derive the time from instructions
  const regex = createRegexFromUnits(Object.keys(timeTextMatchToUnit))
  const unitRank = { hr: 3, min: 2, sec: 1 }
  let matches = {}
  let match
  // Where the last unit that was read ends, and how big it was. A further unit is added to
  // the same duration only when it follows straight on ("1 min 30 sec", "1 min and 30 sec")
  // and is smaller. "1 minute (30 seconds in each direction)" names two durations, and
  // reading it as a minute and a half once doubled into three minutes for a one-minute
  // exercise. Only the first mention is the time (rule 17); the rest of the text is skipped.
  let lastEnd = -1
  let lastRank = Infinity

  while ((match = regex.exec(text)) !== null) {
    const textTimeMatch = match[2]
    const unit = timeTextMatchToUnit[String(textTimeMatch || '').toLowerCase()]
    const value = match[1].padStart(2, '0')
    if (!unit || !value) continue

    const isFirst = lastEnd < 0
    const followsOn = !isFirst && /^\s*(?:and\s+)?$/.test(text.slice(lastEnd, match.index)) && unitRank[unit] < lastRank
    if (!isFirst && !followsOn) continue

    /* Digits on the right of a clock-style "M:SS" state the seconds of a longer duration, so
     * the minutes in front of the colon have to be read too: "rest 1:30 secs" is a minute and a
     * half, not thirty seconds, and so is "1:30 min". A colon with no digits in front of it is
     * the ":30 seconds" shorthand, which already means what it says and is left alone. */
    const clockMinutes = /(\d{1,2}):$/.exec(text.slice(0, match.index))

    if (clockMinutes && (unit === 'sec' || unit === 'min')) {
      if (isFirst) {
        matches.min = clockMinutes[1].padStart(2, '0')
        matches.sec = value
        lastEnd = match.index + match[0].length
        lastRank = unitRank.sec
      }
      continue
    }

    matches[unit] = value
    lastEnd = match.index + match[0].length
    lastRank = unitRank[unit]
  }

  let timeStr
  if (Object.keys(matches).length > 0) {
    // Sum units numerically so "1h 15min" and "1.5 hours" both come out right.
    const toNum = (v) => parseFloat(String(v).replace(',', '.')) || 0
    const totalSec = Math.round(toNum(matches.hr) * 3600 + toNum(matches.min) * 60 + toNum(matches.sec))
    const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0')
    const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0')
    const ss = String(totalSec % 60).padStart(2, '0')
    timeStr = `${hh}:${mm}:${ss}.00`
  }

  const timeRegex = /\b(\d{1,2}):(\d{2}):(\d{2})\b/g // Match HH:MM:SS format
  const timeStrMatch = timeRegex.exec(text)

  if (timeStrMatch) {
    const hr = (timeStrMatch[1] || '0').padStart(2, '0')
    const min = timeStrMatch[2] || '00'
    const sec = timeStrMatch[3] || '00'
    timeStr = `${hr}:${min}:${sec}.00`
  }

  return timeStr
}

/* Exercise instructions only: a time at hour scale is never accepted as one exercise's own
 * time. Exercise timers cap at 59:59, and an hour-scale mention in exercise text is more
 * often about something else (a weekly total, a program length) than about one set, so a
 * misread would inflate the whole workout. Rejecting it falls back to the other estimate
 * sources instead (Lukas's guard, kept through the 08.26 hour-parsing fix). Workout
 * DESCRIPTIONS keep full hour parsing: they are only compared against, never written.
 */
// A time written for one side: "30 seconds in each direction", "20 sec per side", "45s/leg",
// "Hold for 15 seconds on each arm". Up to three words may sit between the time and the
// per-side phrase.
// eslint-disable-next-line
// A time with the per-side words attached to it, in either order.
const PER_SIDE_TIME =
  '\\d+(?:[.,]\\d+)?(?:\\s*(?:-|to)\\s*\\d+(?:[.,]\\d+)?)?\\s*-?\\s*(?:h|hr|hrs|hour|hours|min|mins|minute|minutes|sec|secs|second|seconds|s)\\b'
const PER_SIDE_TIME_RE = new RegExp(
  [
    // time, then the per-side phrase: "20 sec per side", "30 secs (each side)", "45s-each side",
    // "Hold for 15 seconds on each arm", "45s/leg"
    // An opening bracket may sit between them only with nothing else inside it: "30 secs (each
    // side)" is per side, "1 minute (swing each leg ... 30 seconds per leg)" is not, there the
    // per-side time is the later one.
    `${PER_SIDE_TIME}(?:\\s*\\/\\s*(?:${PER_SIDE_NOUNS})s?\\b|(?:\\s*\\(\\s*|[\\s),\\-]*(?:[a-z]+\\s+){0,3}?)(?:per|each|every)\\s+(?:${PER_SIDE_NOUNS})s?\\b)`,
    // the per-side phrase, then the time: "each leg for 1 minute", "per side: 30 sec"
    `(?:per|each|every)\\s+(?:${PER_SIDE_NOUNS})s?\\b[\\s:,\\-]*(?:for\\s+)?${PER_SIDE_TIME}`,
  ].join('|'),
  'i'
)

export const extractExerciseTimeFromText = (text = '') => {
  // Rest is never the exercise's own time (Ex 4), so it is struck out first.
  const readable = stripPerRepHoldMentions(stripPhaseMentions(stripRestMentions(normalizeInstructionText(text))))
  // The flag doubles whatever time is read, so when the text names a time for one side that
  // is the time to read, even if a total for both sides comes first: "1 minute (30 seconds
  // in each direction)" is 30 seconds per side, not a minute per side (C14 of the review).
  const perSide = PER_SIDE_TIME_RE.exec(readable)
  const timeStr = extractTimeValueFromText(perSide ? readable.slice(perSide.index) : readable)
  return timeStr && timeStr.slice(0, 2) !== '00' ? undefined : timeStr
}

/* R8 (2026-09-09). A set count written on the exercise itself: "2 x 20 reps", "3x10",
 * "3 sets of 8", "20 x 3 sets", "Set 1: ... Set 3: ...". Used only when the part has one set
 * or none: 93% of such slots sit in a one-set part, so the text's sets are real there, while a
 * part that already has sets must not be multiplied twice. Returns 0 when none is written. */
export const extractExerciseSetsCount = (rawText = '') => {
  if (!rawText) return 0
  const lower = normalizeInstructionText(rawText).toLowerCase()
  if (/drop\s*-?\s*sets?\b/.test(lower)) return 0
  let m
  if ((m = /(\d+)(?:\s*-\s*(\d+))?\s*(?:sets?|rounds?)\b/.exec(lower)) !== null) {
    const hi = parseInt(m[2] || m[1], 10)
    if (hi > 0 && hi <= 20) return hi
  }
  if ((m = /^\s*(\d+)\s*[x×]\s*\d+(?:\s*-\s*\d+)?\s*(?:reps?\b|$)/.exec(lower)) !== null) {
    const n = parseInt(m[1], 10)
    if (n > 0 && n <= 20) return n
  }
  const numbered = [...lower.matchAll(/\bset\s*(\d+)\s*:/g)].map((x) => parseInt(x[1], 10))
  if (numbered.length > 1) return Math.max(...numbered)
  // "8 (3x)": the bracket is the set count.
  if ((m = /\d\s*\(\s*(\d+)\s*[x×]\s*\)/.exec(lower)) !== null) {
    const n = parseInt(m[1], 10)
    if (n > 0 && n <= 20) return n
  }
  return 0
}

/* Does the text give the parser anything to work with? The batch scripts send an exercise to
 * the model only when this is false, and clear a model value once it becomes true. Kept in the
 * shared block so every copy draws the line in the same place. */
export const instructionsCoverEstimate = (text = '') =>
  extractRepsFromText(text) > 0 ||
  Boolean(extractExerciseTimeFromText(text)) ||
  Boolean(extractTimeFromCoachShorthand(text))

/* ===== SHARED PARSER: END ===== */

/* One exercise's time never exceeds 59:59 (decision 2026-09-10, Lukas's screenshot). Timers stop
 * there, the duration form shows minutes and seconds, and the library per-rep time is capped the
 * same way, so a larger figure cannot be shown or edited: "60:00" read as an hour showed up in
 * the form as 00:00. A reading the sources put above the ceiling ("60:00", "75:00",
 * "900 reps + 60s hold") is held at 59:59 and flagged low confidence, so the coach sees the
 * banner and can set the time by hand instead of a silently truncated figure. */
export const MAX_EXERCISE_SECONDS = 3599

const clampExerciseTime = (result) => {
  const seconds = parseDurationString(result.timeEstValue)
  if (!(seconds > MAX_EXERCISE_SECONDS)) return result
  return {
    ...result,
    timeEstValue: getTimeDurationString(MAX_EXERCISE_SECONDS * 1000),
    isLowConfidence: true,
  }
}

const computeExEstValues = ({ exercise, orgBenchmarks, orgExercises }) => {
  const instructions =
    exercise?.type === 'benchmark' ? orgBenchmarks?.[exercise?.id]?.instructions : exercise?.instructions
  // Stored durations can carry fractional seconds (a backfill script wrote some), while the
  // rest of the dashboard works in whole seconds. Rounding here, where they are read, keeps
  // every downstream figure and comparison on the whole-second spelling.
  const orgTimeEstPerRep = roundDurationString(
    exercise?.type === 'benchmark'
      ? orgBenchmarks?.[exercise?.id]?.timeEst?.perRep
      : orgExercises?.[exercise?.id]?.timeEst?.perRep
  )
  const customTimeEstMethod = exercise?.timeEst?.method
  const customTimeEst = roundDurationString(exercise?.timeEst?.[customTimeEstMethod])
  const timerTime = roundDurationString(exercise?.time)
  const instrTimeEst = extractExerciseTimeFromText(instructions)
  const reps = exercise?.timeEst?.reps || extractRepsFromText(instructions)
  // `??` not `||`: a stored `false` is the coach turning the doubling off, not an absent value.
  const isPerSide = exercise?.timeEst?.isPerSide ?? isPerSideText(instructions)
  // The library's per-rep time is only an estimate once there is a rep count to multiply it
  // by. With no count and no time read, "one rep" is not a guess anybody made: prose such as
  // "other side" or "PNF + activation" showed 4 seconds that way. Such an exercise falls to
  // the 45-second guess with the low-confidence banner, the same as one the library does not
  // know (decision 2026-09-08, rule 9 of the instruction review).
  const usablePerRep = reps > 0 ? orgTimeEstPerRep : undefined
  const timeEstValue = getDefaultEstTimeValue({
    customTimeEst,
    timerTime,
    orgTimeEstPerRep: usablePerRep,
    instrTimeEst,
  })
  const timeEstMethod = getDefaultEstMethodValue({
    timerTime,
    orgTimeEstPerRep: usablePerRep,
    customTimeEstMethod,
    instrTimeEst,
  })
  const isLowConfidence = getIsLowConfidenceEst({ customTimeEst, timerTime, orgTimeEstPerRep, reps, instrTimeEst })
  const noTimer = !timerTime || timerTime === '' || timerTime === '00:00:00.00'
  // What one rep costs before any per-rep instruction is applied.
  const libraryRepSeconds = orgTimeEstPerRep ? parseDurationString(orgTimeEstPerRep) : SHORTHAND_DEFAULT_REP_SECONDS
  const tempoPerRepSec = extractTempoFromText(instructions)

  // ── Tempo / PNF override ──
  // If we have tempo notation (e.g. "1-3-1-1") or PNF timing (e.g. "10s/5s")
  // AND reps, this gives us a higher-quality per-rep estimate than the default.
  // Only override when there's no explicit timer or custom estimate. A written tempo
  // already lists every phase and pause of the rep, so nothing below is added to it.
  if (tempoPerRepSec > 0 && reps > 0 && !customTimeEst && noTimer) {
    return {
      timeEstValue: getTimeDurationString(tempoPerRepSec * 1000),
      timeEstMethod: estimateMethods.perRep,
      reps,
      isPerSide,
      isLowConfidence: false,
    }
  }

  // ── Per-rep pause and phase override (Ex 5b, C1) ──
  // "15 reps (1 sec pause up)", "3 reps, 5 seconds down": the pause or the slow phase is
  // on top of the library rep, so it is added to the per-rep time. When both the up and
  // the down phase are written ("10s up / 10s down") they are the whole rep. Storing the
  // pause alone as the total is how 15 reps once became one second.
  // A hold on every rep (Ex 5, R2) is added the same way; a short "hold ... each rep" would
  // otherwise also read as a pause, so the hold reader takes precedence.
  const holdSeconds = extractPerRepHoldSeconds(instructions)
  const pauseSeconds = holdSeconds > 0 ? 0 : extractPerRepPauseSeconds(instructions)
  const phases = extractPerRepPhaseSeconds(instructions)
  if ((pauseSeconds > 0 || holdSeconds > 0 || phases) && reps > 0 && !customTimeEst && noTimer) {
    const { down = 0, up = 0 } = phases || {}
    const repSeconds = down && up ? down + up : libraryRepSeconds + down + up
    return {
      timeEstValue: getTimeDurationString((repSeconds + pauseSeconds + holdSeconds) * 1000),
      timeEstMethod: estimateMethods.perRep,
      reps,
      isPerSide,
      isLowConfidence: false,
    }
  }

  // ── Coach-shorthand override ──
  // Instructions matching a shorthand shape (holds x reps, rests, dropset
  // series — see the walkthrough above extractTimeFromCoachShorthand) get
  // their composed total instead of the bare literal the plain extractor
  // would store. Same guards as the tempo override: a coach's own timer or
  // custom estimate always wins.
  // A time read from the text for one side ("20 sec per side", "10 Reps Each Side + 20 Sec
  // Hold") is doubled HERE, so the exercise's total time is already the time for both sides
  // and nothing downstream has to know. Only rep-based estimates carry the per-side flag
  // out of this function; on a total time the flag does nothing (decision 2026-09-09).
  // A time the coach typed, or a timer, is never doubled: the coach set the total.
  const doubled = (value) => getTimeDurationString(parseDurationString(value) * 2 * 1000)

  const shorthandTotal = extractTimeFromCoachShorthand(instructions, { perRepSeconds: libraryRepSeconds })
  if (shorthandTotal && !customTimeEst && noTimer) {
    return {
      // A shorthand total is composed from the reps, so it is per side whenever the reps are.
      timeEstValue: isPerSide ? doubled(shorthandTotal) : shorthandTotal,
      timeEstMethod: estimateMethods.perSet,
      reps,
      isPerSide: false,
      isLowConfidence: false,
    }
  }

  if (timeEstMethod === estimateMethods.perSet) {
    const readFromText = Boolean(instrTimeEst) && timeEstValue === instrTimeEst && !customTimeEst && noTimer
    // A stated time doubles only when the per-side phrase is attached to the time itself
    // ("20 sec per side", "30 seconds each leg"). "10 reps each hand ... keep working for 10
    // minutes" is a ten-minute drill: the per-side words belong to the reps, not the time.
    const normalizedInstr = normalizeInstructionText(instructions || '')
    // "1 set per leg, ... 10 seconds": the whole set is per side, so its stated time is too.
    const setIsPerSide = /\bsets?\s+(?:per|each|every)\s+(?:side|leg|arm|hand|direction|way)s?\b/i.test(normalizedInstr)
    const timeIsPerSide = readFromText && isPerSide && (setIsPerSide || PER_SIDE_TIME_RE.test(normalizedInstr))
    return {
      timeEstValue: timeIsPerSide ? doubled(timeEstValue) : timeEstValue,
      timeEstMethod,
      reps,
      isPerSide: false,
      isLowConfidence,
    }
  }

  return {
    timeEstValue,
    timeEstMethod,
    reps,
    isPerSide,
    isLowConfidence,
  }
}

export const getExEstValues = (args) => clampExerciseTime(computeExEstValues(args))

// The range the backend clamps to. Reading a figure outside it would promise a duration that is
// not the one that gets saved.
export const MAX_PER_REP_SECONDS = 3599
export const MIN_PER_REP_SECONDS = 4

const DURATION_PATTERN = /(\d+(?:[.,]\d+)?)\s*-?\s*(min|mins|minute|minutes|sec|secs|second|seconds|s)\b/gi

/**
 * Total seconds stated by durations that stand as their own word, or 0 when there are none.
 *
 * A lone "s" counts as seconds, so digits glued to a word are skipped: the "29s" ending an id like
 * "Exercise mfk29s" is not half a minute. Non-integers ("2.5 seconds", "1,5 minute") are skipped
 * as too ambiguous to read.
 *
 * A colon in front of the digits means they belong to a clock-style duration, which this does not
 * read: "1:30 Min Hold" is a minute and a half, not thirty minutes. Such a name is treated as
 * stating no duration at all, so the estimate comes from the model rather than from a misreading.
 */
const readStandaloneDurationSeconds = (text) => {
  const regex = new RegExp(DURATION_PATTERN.source, DURATION_PATTERN.flags)
  let match
  let minutes = 0
  let seconds = 0
  let found = false

  while ((match = regex.exec(text)) !== null) {
    const charBefore = text[match.index - 1]
    if (charBefore !== undefined && /[a-z0-9:]/i.test(charBefore)) continue

    const value = Number(match[1])
    if (!Number.isInteger(value)) continue

    found = true
    if (timeTextMatchToUnit[match[2].toLowerCase()] === 'min') {
      minutes = value
    } else {
      seconds = value
    }
  }

  return found ? minutes * 60 + seconds : 0
}

/**
 * The rep duration a name states about itself, e.g. "30 minute run" -> "00:30:00.00", or null.
 *
 * The exercise time estimate Cloud Function runs this same extraction on save. Keep the two in
 * step, or the figure shown here stops matching the one stored.
 */
export const getRepDurationFromName = (name) => {
  if (!name) return null

  const seconds = readStandaloneDurationSeconds(name)
  if (!seconds) return null

  const clamped = Math.min(Math.max(seconds, MIN_PER_REP_SECONDS), MAX_PER_REP_SECONDS)
  return getTimeDurationString(clamped * 1000)
}

export const calculateWorkoutEstimate = ({ workout, orgExercises = {}, orgBenchmarks = {}, inflationEnabled }) => {
  let totalSeconds = 0
  let totalPartCount = 0
  let minWktConfidence = 0.2
  let totalWktExCount = 0
  let lowConfidenceWktExCount = 0

  // Overhead tracking
  const distinctExerciseIds = new Set()
  const distinctTimerExerciseIds = new Set() // exercises with in-app countdown timers
  let totalExerciseTransitions = 0
  let timerTransitions = 0 // transitions INTO a timer-based exercise (has 3s countdown)

  if (isEmpty(workout)) {
    return { totalSeconds: 0, isLowConfidence: false, hasLowConfidenceEx: false, lowConfidenceReason: null }
  }

  if (workout?.durationEstOverride) {
    return {
      totalSeconds: parseDurationString(workout?.durationEstOverride),
      isLowConfidence: false,
      hasLowConfidenceEx: false,
      lowConfidenceReason: null,
    }
  }

  if (!Array.isArray(workout.parts)) {
    return { totalSeconds: 0, isLowConfidence: false, hasLowConfidenceEx: false, lowConfidenceReason: null }
  }

  for (let part of workout.parts) {
    if (!part) continue

    totalPartCount++
    const sets = part.sets || extractSetsCount(part?.instructions || '') || 1
    let partSeconds = 0

    // If the entire part is explicitly a rest block
    if (part.type === 'rest' && part.time) {
      const restSec = parseDurationString(part.time) * sets
      totalSeconds += restSec
      continue
    }

    if (!part?.exercises || !part?.exercises?.length) {
      continue
    }

    // Normal part with exercises.
    //
    // Null slots (where an exercise was removed from the middle of a part) and rows the coach
    // has not filled in yet are dropped up front, so they are left out of the work total, the
    // overhead, the guess count and the transitions below alike. An exercise typed in without
    // matching the library is stored with id '' and is kept — see isBlankExerciseRow.
    const filledExercises = part.exercises.filter((ex) => !isBlankExerciseRow(ex))

    for (let ex of filledExercises) {
      // Track distinct exercises for overhead calculation
      distinctExerciseIds.add(ex.id)

      // Track timer-based exercises separately (have in-app countdown → less overhead)
      const isTimerBased = ex?.time && ex.time !== '' && ex.time !== '00:00:00.00'
      if (isTimerBased) {
        distinctTimerExerciseIds.add(ex.id)
      }
      let exWorkSeconds = 0

      const { timeEstValue, timeEstMethod, reps, isPerSide, isLowConfidence } = getExEstValues({
        exercise: ex,
        orgBenchmarks,
        orgExercises,
      })
      const exReps = timeEstMethod === estimateMethods.perSet ? 1 : Math.max(reps, 1)
      // Only a rep-based estimate doubles here. A time written for one side is already
      // doubled inside getExEstValues, and a timer or a coach's total covers both sides.
      const perSideMultiplier = isPerSide && timeEstMethod === estimateMethods.perRep ? 2 : 1
      exWorkSeconds += exReps * parseDurationString(timeEstValue) * perSideMultiplier

      totalWktExCount++
      if (isLowConfidence) {
        lowConfidenceWktExCount++
      }

      // Add rest if present
      let exRestSeconds = 0
      if (ex.rest) {
        exRestSeconds = parseDurationString(ex.rest)
      }
      // A set count written on the exercise counts when the part has none of its own (R8).
      const exInstr = ex?.type === 'benchmark' ? orgBenchmarks?.[ex?.id]?.instructions : ex?.instructions
      const exSets = sets <= 1 ? Math.max(extractExerciseSetsCount(exInstr), 1) : 1
      partSeconds += (exWorkSeconds + exRestSeconds) * exSets
    }

    // Count exercise transitions within this part, split by timer vs non-timer.
    // A transition INTO a timer exercise gets reduced overhead (app has 3s countdown).
    // Counted over the filled rows only: nobody moves through a row with nothing in it.
    const exercisesInPart = filledExercises.length
    if (exercisesInPart > 1) {
      let partTimerTransitions = 0
      for (let i = 1; i < exercisesInPart; i++) {
        const targetEx = filledExercises[i]
        const targetHasTimer = targetEx?.time && targetEx.time !== '' && targetEx.time !== '00:00:00.00'
        if (targetHasTimer) {
          partTimerTransitions++
        }
      }
      const partTotalTransitions = exercisesInPart - 1
      timerTransitions += partTimerTransitions * sets
      totalExerciseTransitions += partTotalTransitions * sets
    }

    // Multiply by sets
    totalSeconds += partSeconds * sets
  }

  // Add transitions between parts
  if (totalPartCount > 1) {
    totalExerciseTransitions += totalPartCount - 1
  }

  // ====== OVERHEAD CALCULATION ======
  if (inflationEnabled) {
    const distinctExCount = distinctExerciseIds.size

    let setupSeconds
    if (totalSeconds < SHORT_WKT_THRESHOLD) {
      setupSeconds = OVERHEAD_SETUP.short
    } else if (totalSeconds < MEDIUM_WKT_THRESHOLD) {
      setupSeconds = OVERHEAD_SETUP.medium
    } else {
      setupSeconds = OVERHEAD_SETUP.long
    }
    const familiaritySeconds = distinctExCount * FAMILIARITY_SECONDS_PER_EXERCISE
    // Timer-based exercises don't need form cue time — user won't pause the countdown to read cues
    const distinctNonTimerExCount = distinctExCount - distinctTimerExerciseIds.size
    const formCueSeconds = distinctNonTimerExCount * FORM_CUE_SECONDS_PER_EXERCISE
    // Timer exercises already have a 3s in-app countdown, so transitions into them are minimal
    const nonTimerTransitions = totalExerciseTransitions - timerTransitions
    const transitionSeconds =
      timerTransitions * TIMER_TRANSITION_SECONDS_PER_CHANGE + nonTimerTransitions * TRANSITION_SECONDS_PER_CHANGE

    const overheadSeconds = setupSeconds + familiaritySeconds + formCueSeconds + transitionSeconds
    totalSeconds += overheadSeconds
  }

  const lowConfidenceRatio = totalWktExCount > 0 ? lowConfidenceWktExCount / totalWktExCount : 1
  let isLowConfidence = false
  let lowConfidenceReason = null

  if (lowConfidenceRatio > minWktConfidence) {
    isLowConfidence = true
    lowConfidenceReason = `More than ${minWktConfidence * 100}% of exercises have unreliable duration estimates.`
  }

  if (totalSeconds > MAX_WKT_DURATION_SECONDS) {
    isLowConfidence = true
    if (lowConfidenceReason) {
      lowConfidenceReason = lowConfidenceReason + ` Workout duration is more than 2 hours.`
    } else {
      lowConfidenceReason = `Workout duration is more than 2 hours.`
    }
  }

  // If the workout has an authoritative duration (from timeEst or instructions),
  // compare it against our estimate. Flag as low confidence if off by >10%.
  const wktTimeEstStr = workout?.timeEst && workout.timeEst !== '00:00:00.00' ? workout.timeEst : null
  const wktInstrTimeEstStr = !wktTimeEstStr ? extractTimeValueFromText(workout?.instructions) : null
  const authoritativeDurationStr = wktTimeEstStr || wktInstrTimeEstStr
  if (authoritativeDurationStr) {
    const authoritativeSeconds = parseDurationString(authoritativeDurationStr)
    if (authoritativeSeconds > 0 && totalSeconds > 0) {
      const deviationRatio = Math.abs(totalSeconds - authoritativeSeconds) / authoritativeSeconds
      if (deviationRatio > LOW_CONFIDENCE_DEVIATION_THRESHOLD) {
        isLowConfidence = true
        const source = wktTimeEstStr ? 'legacy time estimate' : 'time in the workout description'
        // Estimates recalculated by the dashboard (any coach edit clears the
        // 'script' stamp) are withheld at publish until the coach reviews —
        // say so. Script-written ones are already live in the app; a plain
        // review prompt is enough there.
        const consequence =
          workout?.durationEstSource === 'script'
            ? 'Please review.'
            : "Users won't see the AI estimate until you review."
        const newReason = `AI-generated time estimate is more than ${
          LOW_CONFIDENCE_DEVIATION_THRESHOLD * 100
        }% off from the ${source}. ${consequence}`
        lowConfidenceReason = lowConfidenceReason ? `${lowConfidenceReason} ${newReason}` : newReason
      }
    }
  }

  return {
    totalSeconds,
    isLowConfidence,
    hasLowConfidenceEx: lowConfidenceWktExCount > 0,
    lowConfidenceReason,
  }
}

// Whole-minute display, the way the mobile app formats any duration
// (under a minute shows as 1min; seconds past 30 round the minute up).
export const getMobileExactMinutes = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return null
  const totalMinutes = totalSeconds / 60
  if (totalMinutes < 1) return '1min'
  const whole = Math.floor(totalMinutes)
  const mins = totalSeconds % 60 > 30 ? whole + 1 : whole
  const hrs = Math.floor(mins / 60)
  const rem = mins % 60
  if (hrs > 0 && rem > 0) return `${hrs}hr ${rem}min`
  if (hrs > 0) return `${hrs}hr`
  return `${rem}min`
}

// What the member sees for an AUTO estimate: exact minutes under 15min,
// rounded UP to the next 5 minutes from there. Coach overrides display
// exactly as written (use getMobileExactMinutes for those).
export const getMobileDisplayTime = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return null
  const totalMinutes = totalSeconds / 60
  if (totalMinutes < 15) return getMobileExactMinutes(totalSeconds)
  const mins = Math.ceil(totalMinutes / 5) * 5
  const hrs = Math.floor(mins / 60)
  const rem = mins % 60
  if (hrs > 0 && rem > 0) return `${hrs}hr ${rem}min`
  if (hrs > 0) return `${hrs}hr`
  return `${rem}min`
}

export const calcWktEstInDurationForm = ({ workout, exReps, exTimes, exEstMethods, exIsPerSide, inflationEnabled }) => {
  let totalSeconds = 0
  let totalRestSeconds = 0
  let totalPartCount = 0

  // Overhead tracking
  const distinctExerciseIds = new Set()
  const distinctTimerExerciseIds = new Set() // exercises with in-app countdown timers
  let totalExerciseTransitions = 0
  let timerTransitions = 0 // transitions INTO a timer-based exercise (has 3s countdown)

  if (!Array.isArray(workout?.parts)) {
    return { totalRestSeconds: 0, totalWorkSeconds: 0, totalOverheadSeconds: 0, totalSeconds: 0 }
  }

  workout.parts.forEach((part, partIdx) => {
    if (!part) return

    totalPartCount++
    const sets = part.sets || extractSetsCount(part?.instructions || '') || 1
    let partSeconds = 0
    let partRestSeconds = 0

    // If the entire part is explicitly a rest block
    if (part.type === 'rest' && part.time) {
      const restSec = parseDurationString(part.time) * sets
      totalSeconds += restSec
      totalRestSeconds += restSec
      return
    }

    if (!part?.exercises || !part?.exercises?.length) {
      return
    }

    // Normal part with exercises.
    //
    // Same rule as calculateWorkoutEstimate: null slots and rows the coach has not filled in
    // yet are dropped, while an exercise typed in without matching the library (id '') is
    // kept. Each row carries its own index along with it, because the figures this function
    // reads — exTimes, exEstMethods, exReps, exIsPerSide — are keyed by the exercise's
    // position in the original array, so the rows cannot simply be renumbered.
    const filledExercises = part.exercises
      .map((ex, exIdx) => ({ ex, exIdx }))
      .filter(({ ex }) => !isBlankExerciseRow(ex))

    filledExercises.forEach(({ ex, exIdx }) => {
      // Track distinct exercises for overhead calculation
      distinctExerciseIds.add(ex.id)

      // Track timer-based exercises separately (have in-app countdown → less overhead)
      const isTimerBased = ex?.time && ex.time !== '' && ex.time !== '00:00:00.00'
      if (isTimerBased) {
        distinctTimerExerciseIds.add(ex.id)
      }
      let exWorkSeconds = 0

      const timeEstValue = exTimes?.[`${partIdx}-${exIdx}`]
      const estMethod = exEstMethods?.[`${partIdx}-${exIdx}`]
      // A per-set "total time" already covers every rep in the set, so only the per-rep
      // method multiplies by the rep count — same rule as calculateWorkoutEstimate.
      const reps = estMethod === estimateMethods.perSet ? 1 : exReps?.[`${partIdx}-${exIdx}`]
      // Same rule as calculateWorkoutEstimate: only a rep-based estimate doubles. A time
      // written for one side arrives already doubled, and the box does nothing on a total.
      const perSideMultiplier = exIsPerSide?.[`${partIdx}-${exIdx}`] && estMethod === estimateMethods.perRep ? 2 : 1
      exWorkSeconds += reps * parseDurationString(timeEstValue) * perSideMultiplier

      // Add rest if present
      let exRestSeconds = 0
      if (ex.rest) {
        exRestSeconds = parseDurationString(ex.rest)
      }
      // A set count written on the exercise counts when the part has none of its own (R8).
      const exSets = sets <= 1 ? Math.max(extractExerciseSetsCount(ex?.instructions), 1) : 1
      partSeconds += (exWorkSeconds + exRestSeconds) * exSets
      partRestSeconds += exRestSeconds * exSets
    })

    // Count exercise transitions within this part, split by timer vs non-timer.
    // A transition INTO a timer exercise gets reduced overhead (app has 3s countdown).
    // Counted over the filled rows only: nobody moves through a row with nothing in it.
    const exercisesInPart = filledExercises.length
    if (exercisesInPart > 1) {
      let partTimerTransitions = 0
      for (let i = 1; i < exercisesInPart; i++) {
        const targetEx = filledExercises[i].ex
        const targetHasTimer = targetEx?.time && targetEx.time !== '' && targetEx.time !== '00:00:00.00'
        if (targetHasTimer) {
          partTimerTransitions++
        }
      }
      const partTotalTransitions = exercisesInPart - 1
      timerTransitions += partTimerTransitions * sets
      totalExerciseTransitions += partTotalTransitions * sets
    }

    // Multiply by sets
    totalSeconds += partSeconds * sets
    totalRestSeconds += partRestSeconds * sets
  })

  // Add transitions between parts
  if (totalPartCount > 1) {
    totalExerciseTransitions += totalPartCount - 1
  }

  // ====== OVERHEAD CALCULATION ======
  let totalOverheadSeconds = 0
  if (inflationEnabled) {
    const distinctExCount = distinctExerciseIds.size

    let setupSeconds
    if (totalSeconds < SHORT_WKT_THRESHOLD) {
      setupSeconds = OVERHEAD_SETUP.short
    } else if (totalSeconds < MEDIUM_WKT_THRESHOLD) {
      setupSeconds = OVERHEAD_SETUP.medium
    } else {
      setupSeconds = OVERHEAD_SETUP.long
    }
    const familiaritySeconds = distinctExCount * FAMILIARITY_SECONDS_PER_EXERCISE
    // Timer-based exercises don't need form cue time — user won't pause the countdown to read cues
    const distinctNonTimerExCount = distinctExCount - distinctTimerExerciseIds.size
    const formCueSeconds = distinctNonTimerExCount * FORM_CUE_SECONDS_PER_EXERCISE
    // Timer exercises already have a 3s in-app countdown, so transitions into them are minimal
    const nonTimerTransitions = totalExerciseTransitions - timerTransitions
    const transitionSeconds =
      timerTransitions * TIMER_TRANSITION_SECONDS_PER_CHANGE + nonTimerTransitions * TRANSITION_SECONDS_PER_CHANGE

    totalOverheadSeconds = setupSeconds + familiaritySeconds + formCueSeconds + transitionSeconds
    totalSeconds += totalOverheadSeconds
  }

  const totalWorkSeconds = totalSeconds - totalRestSeconds - totalOverheadSeconds
  return { totalRestSeconds, totalWorkSeconds, totalOverheadSeconds, totalSeconds }
}
